import * as THREE from 'three';
import { createScene } from '../core/scene.js';
import { createRenderer, handleResize } from '../core/renderer.js';
import { createCamera } from '../core/camera.js';
import { FirstPersonControls } from '../core/controls.js';
import { Lighting } from './Lighting.js';
import { Maze } from './Maze.js';
import { VideoPainting } from './VideoPainting.js';
import { UI } from './UI.js';
import { AssetLoader } from '../utils/AssetLoader.js';
import { PostProcessing } from '../utils/PostProcessing.js';
import { MazeGenerator } from '../utils/MazeGenerator.js';
import { ConfigGenerator } from '../utils/ConfigGenerator.js';
import { IntroMessage } from './IntroMessage.js';
import { Minimap } from './Minimap.js';
import mazeSettings from '../config/maze-settings.json';

export class Gallery {
  constructor(canvas) {
    this.canvas = canvas;
    this.scene = null;
    this.camera = null;
    this.renderer = null;
    this.controls = null;
    this.lighting = null;
    this.maze = null;
    this.paintings = [];
    this.ui = null;
    this.assetLoader = null;
    this.postProcessing = null;
    this.audioListener = null;
    this.config = null;
    this.introMessage = null;
    this.startPosition = null;
    this.minimap = null;
    this.mazeLayout = null;

    this.clock = new THREE.Clock();
    this.isRunning = false;

    // Dynamic video loading
    this.currentRoomId = null;
    this.loadedRooms = new Set(); // Track which rooms have loaded videos
    this.loadDistance = 1; // Load videos for current room + 1 room away
    this.lastLoadCheck = 0; // Timestamp of last load check
  }

  async init() {
    // Initialize UI
    this.ui = new UI();
    this.ui.updateLoadingText('Initializing gallery...');

    // Initialize asset loader
    this.assetLoader = new AssetLoader();
    this.assetLoader.setOnProgress((progress) => {
      this.ui.updateLoadingProgress(progress);
    });

    // Load configuration (procedural or static)
    try {
      this.ui.updateLoadingText('Loading configuration...');

      // Use imported maze settings (bundled during build)
      // Check if procedural generation is enabled
      if (mazeSettings.enableProcedural) {
        this.ui.updateLoadingText('Generating procedural maze...');
        console.log('Procedural generation enabled');

        // Load video manifest
        let videoList;
        try {
          videoList = await ConfigGenerator.loadVideoManifest(
            mazeSettings.videoManifestPath || '/videos/manifest.json'
          );
        } catch (e) {
          console.warn('Video manifest not found, using fallback videos');
          videoList = ConfigGenerator.generateFallbackVideoList(mazeSettings.paintingCount || 9);
        }

        // Determine painting count
        const paintingCount = videoList.length;
        console.log(`Generating maze for ${paintingCount} paintings`);

        // Generate maze layout
        const generator = new MazeGenerator({
          paintingCount: paintingCount,
          cellSize: mazeSettings.cellSize,
          corridorWidth: mazeSettings.corridorWidth,
          roomPadding: mazeSettings.roomPadding,
          seed: mazeSettings.seed || Date.now(),
          minPaintingDistance: mazeSettings.minPaintingDistance,
          minPaintingsPerRoom: mazeSettings.minPaintingsPerRoom || 1,
          maxPaintingsPerRoom: mazeSettings.maxPaintingsPerRoom || 3,
          minEntrancesPerRoom: mazeSettings.minEntrancesPerRoom || 1,
          maxEntrancesPerRoom: mazeSettings.maxEntrancesPerRoom || 3
        });

        this.mazeLayout = generator.generate();

        // Validate painting spacing
        generator.validatePaintingSpacing();

        // Generate final configuration
        this.config = ConfigGenerator.generateFromVideos(videoList, this.mazeLayout, {
          defaultScale: mazeSettings.defaultScale,
          shuffleVideos: mazeSettings.shuffleVideos,
          seed: mazeSettings.seed || Date.now()
        });

        // Log generation info
        console.log('Procedural generation complete:', {
          seed: this.mazeLayout.seed,
          gridSize: this.mazeLayout.gridSize,
          totalSize: this.mazeLayout.totalSize,
          paintings: this.config.paintings.length,
          walls: this.config.walls.length
        });

        // Optionally save generated config for debugging
        if (mazeSettings.debug?.enabled) {
          console.log('Generated config:', this.config);
        }
      } else {
        // Load static configuration
        this.ui.updateLoadingText('Loading static configuration...');
        this.config = await this.assetLoader.loadConfig('/src/config/gallery-config.json');
        console.log('Using static configuration');
      }
    } catch (error) {
      console.error('Failed to load configuration:', error);
      this.ui.updateLoadingText('Error loading configuration');
      return;
    }

    // Create Three.js components
    this.ui.updateLoadingText('Creating 3D scene...');
    this.scene = createScene();
    this.camera = createCamera();
    this.renderer = createRenderer(this.canvas);

    // Create audio listener and attach to camera
    this.audioListener = new THREE.AudioListener();
    this.camera.add(this.audioListener);
    console.log('Audio listener created and attached to camera');

    // Set up post-processing
    this.postProcessing = new PostProcessing(this.renderer, this.scene, this.camera);

    // Handle window resize
    handleResize(this.camera, this.renderer);
    window.addEventListener('resize', () => {
      this.postProcessing.setSize(window.innerWidth, window.innerHeight);
    });

    // Create lighting system
    this.ui.updateLoadingText('Setting up lighting...');
    this.lighting = new Lighting(this.scene);

    // Create maze
    this.ui.updateLoadingText('Building maze...');
    this.maze = new Maze(this.scene, this.config);

    // Set up controls
    this.ui.updateLoadingText('Configuring controls...');
    this.controls = new FirstPersonControls(this.camera, this.renderer.domElement);
    this.controls.checkCollision = (position) => this.maze.checkCollision(position);

    // Set camera to start position
    this.startPosition = { ...this.config.startPosition };
    this.camera.position.set(
      this.config.startPosition.x,
      this.config.startPosition.y,
      this.config.startPosition.z
    );

    // Skip video preloading - videos will load dynamically based on room proximity
    this.ui.updateLoadingText('Preparing video gallery...');

    // Create paintings
    this.ui.updateLoadingText('Creating paintings...');
    this.config.paintings.forEach((paintingData) => {
      const painting = new VideoPainting(this.scene, paintingData, this.lighting, this.audioListener);
      this.paintings.push(painting);
    });

    // Create intro message floating in front of player
    this.introMessage = new IntroMessage(this.scene, this.startPosition, 0);

    // Create minimap if maze data is available
    if (this.mazeLayout) {
      this.minimap = new Minimap(this.mazeLayout);
    }

    // Show start options and wait for user to click start
    this.ui.showStartOptions();

    // Set up start button callback
    this.ui.onStart(() => {
      this.ui.hideLoadingScreen();
      this.start();
    });

    console.log('Gallery initialized successfully');
  }

  start() {
    if (this.isRunning) return;
    this.isRunning = true;

    // Load initial videos for starting room
    this.updateVideoLoading().then(() => {
      console.log('Initial video loading complete');
    });

    this.animate();
  }

  stop() {
    this.isRunning = false;
  }

  animate() {
    if (!this.isRunning) return;

    requestAnimationFrame(() => this.animate());

    const delta = this.clock.getDelta();
    // const elapsedTime = this.clock.getElapsedTime(); // Not needed without post-processing

    // Update controls
    this.controls.update(delta);

    // Update video loading based on room position (non-blocking)
    // Only check every ~1 second to avoid excessive checks
    if (!this.lastLoadCheck || Date.now() - this.lastLoadCheck > 1000) {
      this.lastLoadCheck = Date.now();
      this.updateVideoLoading().catch(err => {
        console.error('Error updating video loading:', err);
      });
    }

    // Update intro message (fade out when player moves)
    if (this.introMessage && this.introMessage.isVisible) {
      this.introMessage.checkPlayerDistance(this.camera.position, this.startPosition, 2);
      const stillVisible = this.introMessage.update(delta);
      if (!stillVisible) {
        this.introMessage.dispose();
        this.introMessage = null;
      }
    }

    // Update paintings (proximity-based video playback with visibility check)
    this.paintings.forEach((painting) => {
      painting.update(this.camera);
    });

    // Update minimap
    if (this.minimap && this.ui.isMinimapEnabled()) {
      this.minimap.updatePlayer(
        this.camera.position.x,
        this.camera.position.z,
        this.camera.rotation.y
      );
      this.minimap.draw();
    }

    // Update UI (painting info)
    // NOTE: Painting info is now displayed as 3D text on the wall next to each painting
    // No need to update HTML overlay
    // if (this.controls.isLocked) {
    //   this.ui.updatePaintingInfo(this.camera.position, this.paintings, this.camera, this.renderer);
    // }

    // Update post-processing effects
    // this.postProcessing.update(elapsedTime);

    // Render scene with post-processing (DISABLED - using direct render)
    // this.postProcessing.render();

    // Direct rendering without post-processing effects
    this.renderer.render(this.scene, this.camera);
  }

  /**
   * Get the room ID that contains the given position
   * Returns null if not in any room or if no maze layout
   */
  getRoomAtPosition(position) {
    if (!this.mazeLayout || !this.mazeLayout.rooms) {
      return null;
    }

    for (const room of this.mazeLayout.rooms) {
      // Check if position is within room bounds
      if (position.x >= room.x && position.x <= room.x + room.width &&
          position.z >= room.z && position.z <= room.z + room.depth) {
        return room.id;
      }
    }

    return null; // In a corridor or outside
  }

  /**
   * Get all rooms connected to the given room ID
   */
  getAdjacentRooms(roomId) {
    if (!this.mazeLayout || !this.mazeLayout.rooms || !this.mazeLayout.corridors) {
      return [];
    }

    const adjacentRoomIds = new Set();

    // Find all corridors connected to this room
    for (const corridor of this.mazeLayout.corridors) {
      if (corridor.from === roomId) {
        adjacentRoomIds.add(corridor.to);
      } else if (corridor.to === roomId) {
        adjacentRoomIds.add(corridor.from);
      }
    }

    return Array.from(adjacentRoomIds);
  }

  /**
   * Get all rooms within the specified distance (in room hops)
   */
  getRoomsWithinDistance(roomId, distance) {
    if (!roomId || distance < 0) {
      return [];
    }

    const roomsToLoad = new Set([roomId]);
    let currentLevel = [roomId];

    for (let i = 0; i < distance; i++) {
      const nextLevel = [];
      for (const currentRoomId of currentLevel) {
        const adjacent = this.getAdjacentRooms(currentRoomId);
        for (const adjRoom of adjacent) {
          if (!roomsToLoad.has(adjRoom)) {
            roomsToLoad.add(adjRoom);
            nextLevel.push(adjRoom);
          }
        }
      }
      currentLevel = nextLevel;
    }

    return Array.from(roomsToLoad);
  }

  /**
   * Get paintings in the specified rooms
   */
  getPaintingsInRooms(roomIds) {
    if (!this.mazeLayout || !this.mazeLayout.rooms) {
      return [];
    }

    const paintingsToLoad = [];

    for (const roomId of roomIds) {
      const room = this.mazeLayout.rooms.find(r => r.id === roomId);
      if (room && room.paintings) {
        // Match paintings by position
        for (const paintingSlot of room.paintings) {
          const painting = this.paintings.find(p =>
            Math.abs(p.data.position.x - paintingSlot.position.x) < 0.1 &&
            Math.abs(p.data.position.z - paintingSlot.position.z) < 0.1
          );
          if (painting) {
            paintingsToLoad.push(painting);
          }
        }
      }
    }

    return paintingsToLoad;
  }

  /**
   * Update which videos are loaded based on player position
   */
  async updateVideoLoading() {
    // Only do dynamic loading if we have maze layout (procedural mode)
    if (!this.mazeLayout || !this.mazeLayout.rooms) {
      // Static mode - load all videos on first call
      if (this.loadedRooms.size === 0) {
        console.log('Static mode: loading all videos');
        const loadPromises = this.paintings.map(p => p.loadVideo());
        await Promise.all(loadPromises);
        this.loadedRooms.add('all');
      }
      return;
    }

    const currentRoom = this.getRoomAtPosition(this.camera.position);

    // If we changed rooms or this is the first update, recalculate
    if (currentRoom !== this.currentRoomId) {
      this.currentRoomId = currentRoom;

      if (currentRoom) {
        console.log('Player in room:', currentRoom);

        // Get rooms to load
        const roomsToLoad = this.getRoomsWithinDistance(currentRoom, this.loadDistance);
        console.log('Loading videos for rooms:', roomsToLoad);

        // Load videos for nearby rooms
        const paintingsToLoad = this.getPaintingsInRooms(roomsToLoad);
        const loadPromises = paintingsToLoad.map(p => p.loadVideo());
        await Promise.all(loadPromises);

        // Unload videos from far rooms
        const roomsToUnload = Array.from(this.loadedRooms).filter(
          roomId => !roomsToLoad.includes(roomId)
        );

        for (const roomId of roomsToUnload) {
          const paintingsToUnload = this.getPaintingsInRooms([roomId]);
          paintingsToUnload.forEach(p => p.unloadVideo());
        }

        // Update loaded rooms set
        this.loadedRooms.clear();
        roomsToLoad.forEach(id => this.loadedRooms.add(id));
      }
    }
  }

  dispose() {
    this.isRunning = false;

    // Dispose of all components
    if (this.introMessage) {
      this.introMessage.dispose();
      this.introMessage = null;
    }

    if (this.minimap) {
      this.minimap.dispose();
      this.minimap = null;
    }

    if (this.postProcessing) {
      this.postProcessing.dispose();
    }

    if (this.maze) {
      this.maze.dispose();
    }

    if (this.lighting) {
      this.lighting.dispose();
    }

    this.paintings.forEach((painting) => {
      painting.dispose();
    });
    this.paintings = [];

    if (this.renderer) {
      this.renderer.dispose();
    }
  }
}
